import React, { ReactNode, useEffect, useState, useMemo } from "react";
import { useInfiniteQuery, UseInfiniteQueryResult, useQuery } from "react-query";
import {
  fetchNotifications,
  fetchNotificationStat,
} from "shared/components/Notification/NotificationServices";
import { playNotificationSound } from "utils/sound";
import { useDecodedUser } from "./DecodedUser";

const AppNotificationsContext = React.createContext<
  UseInfiniteQueryResult<any, Error> & { result: any[]; count: number }
  // @ts-ignore
>({
  result: [],
  count: 0,
});

const AppNotificationsProvider = ({ children }: { children: ReactNode }) => {
  const [prevCount, setPrevCount] = useState(0);
  const { data: user } = useDecodedUser();
  const queryData = useInfiniteQuery<any, Error>(
    ["fetching-user-notification", { page: 1, pageSize: 10 }],
    fetchNotifications,
    {
      enabled: !!user?.iat,
      getNextPageParam: (lastPage: any, pages: any) => {
        if (lastPage?.current_page < lastPage?.total_pages) {
          return {
            page: Number(lastPage?.current_page || "1") + 1,
            pageSize: 10,
          };
        }
        return undefined;
      },
    }
  );

  const notificationList = useMemo(
    () => queryData.data?.pages.flatMap((x: any) => x.results) ?? [],
    [queryData.data]
  );

  const { refetch, isFetching } = queryData;

  const { data: countRes } = useQuery<any, Error>(
    ["fetching-user-notification-stat"],
    fetchNotificationStat,
    {
      enabled: !!user?.iat,
      refetchOnMount: true,
      refetchInterval: 15000,
      refetchIntervalInBackground: true,
      retry: true,
      retryDelay: 5000,
    }
  );

  useEffect(() => {
    const count = countRes?.data?.count;
    if (count !== undefined && count !== 0 && count > prevCount) {
      playNotificationSound();
      setPrevCount(count);
    }
  }, [countRes?.data?.count, prevCount]);

  useEffect(() => {
    if (isFetching || !user?.iat) return;
    refetch();
  }, [countRes?.data?.count, user?.iat]);

  const contextValue = useMemo(
    () => ({
      ...queryData,
      result: notificationList,
      count: countRes?.data?.count,
    }),
    [queryData, notificationList, countRes?.data?.count]
  );

  return (
    <AppNotificationsContext.Provider value={contextValue}>
      {children}
    </AppNotificationsContext.Provider>
  );
};

export const useAppNotifications = () => {
  const _c = React.useContext(AppNotificationsContext);

  return _c;
};

export default AppNotificationsProvider;
